/**
 * Run configuration for the scheduled-enrichment routes — the nested form the
 * backend expects on `POST enrichments/scheduled/jobs` and its three triggers:
 *
 * ```jsonc
 * {
 *   "contentAttributes": ["title", "structured_title", "description"],
 *   "searchDescriptors": { "enabled": false, "phraseDescriptions": false },
 *   "scoring": { "preScoring": true, "postScoring": true }
 * }
 * ```
 *
 * Distinct from `EnrichmentPostStepFlags` (`~/types/enrichment-flags`), which is
 * the **flat** form and remains correct for the routes that still specify it:
 * `catalog-operations/trigger` (nested on its `useCase` object, per the backend UI
 * contract), the experiment triggers and Trial creation. Both shapes describe the
 * same four decisions; only the scheduled routes take the nested one.
 *
 * See `docs/enrichment-run-panel.md`.
 */

/** The `searchDescriptors` object. `phraseDescriptions` is the dependent step. */
export interface SearchDescriptorsConfig {
  /** Runs `SEARCH_DESCRIPTORS`, generating the phrases the dependent step consumes. */
  enabled: boolean;
  /** Runs `PHRASE_DESCRIPTIONS`. Meaningless without `enabled` — the step would
   *  execute against nothing and report a misconfiguration rather than failing, so
   *  the pairing is enforced client-side by
   *  {@link resolveSearchDescriptorsConfig}. */
  phraseDescriptions: boolean;
}

/** The `scoring` object. Pre and post are the same step at two phases. */
export interface ScoringConfig {
  /** Scores the retailer's untouched product, for a baseline. */
  preScoring: boolean;
  /** Scores the enrichment output, so the lift can be measured. */
  postScoring: boolean;
}

/**
 * The run-configuration slice carried by every scheduled-enrichment request.
 *
 * Each key is independently optional, and **omitting one means "no choice
 * recorded"** — the server's default applies. This is where the tri-state that
 * used to live per flat boolean now lives: object present = explicit choice for
 * both of its fields, object absent = server decides. An empty `{}` therefore
 * configures nothing, which is what an unhydrated browser should send.
 */
export interface EnrichmentRunConfig {
  /** Selected content-attribute keys in stable config-service form. */
  contentAttributes?: string[];
  searchDescriptors?: SearchDescriptorsConfig;
  scoring?: ScoringConfig;
  /**
   * google-agent retailer prompt slug (e.g. `hibbett`). Absent = the shared prompt. Persisted on
   * the scheduled job, so every later trigger of the run inherits it server-side.
   */
  retailer?: string;
  /**
   * Locales to deliver besides en-US, each translated from the English. Absent = English only.
   * Persisted on the scheduled job like `retailer`, so later triggers inherit it server-side.
   */
  targetLocales?: string[];
}

/**
 * Applies the one hard dependency: `PHRASE_DESCRIPTIONS` consumes
 * `SEARCH_DESCRIPTORS` output, so it cannot run alone.
 *
 * Nesting makes the relationship obvious but does not enforce it — the server
 * accepts `{ enabled: false, phraseDescriptions: true }` and reports a
 * misconfiguration after the fact, which reads as "ran and failed" rather than
 * "not configured". Mirrors `resolveEnrichmentFlagDependencies` for the flat form.
 */
export function resolveSearchDescriptorsConfig(
  config: SearchDescriptorsConfig,
): SearchDescriptorsConfig {
  if (!config.enabled && config.phraseDescriptions) return { ...config, phraseDescriptions: false };
  return config;
}

/**
 * Normalises a run config before it goes on the wire: resolves the descriptor
 * dependency and drops keys that carry no choice, so a request never contains an
 * invalid pair or an empty `contentAttributes` array that would read as "enrich
 * nothing" rather than "unspecified".
 */
export function compactEnrichmentRunConfig(config: EnrichmentRunConfig): EnrichmentRunConfig {
  return {
    ...(config.contentAttributes && config.contentAttributes.length > 0
      ? { contentAttributes: config.contentAttributes }
      : {}),
    ...(config.searchDescriptors
      ? { searchDescriptors: resolveSearchDescriptorsConfig(config.searchDescriptors) }
      : {}),
    ...(config.scoring ? { scoring: config.scoring } : {}),
    ...(config.retailer ? { retailer: config.retailer } : {}),
    ...(config.targetLocales?.length ? { targetLocales: config.targetLocales } : {}),
  };
}
